import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getAdminInsights } from "@/lib/insights.functions";
import { PageHeader } from "@/components/admin/AdminPrimitives";
import { AlertTriangle, Lightbulb, TrendingUp, CheckCircle2, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/insights")({
  head: () => ({ meta: [{ title: "Insights — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: InsightsPage,
});

const KIND = {
  gargalo:     { icon: AlertTriangle, color: "text-red-600",       bg: "bg-red-50",             border: "border-red-200" },
  atencao:     { icon: AlertTriangle, color: "text-[var(--gold)]", bg: "bg-[var(--gold)]/10",   border: "border-[var(--gold)]/30" },
  oportunidade:{ icon: Lightbulb,     color: "text-[var(--navy)]", bg: "bg-[var(--navy)]/5",    border: "border-[var(--navy)]/20" },
  positivo:    { icon: CheckCircle2,  color: "text-[var(--green)]",bg: "bg-[var(--green)]/10",  border: "border-[var(--green)]/30" },
} as const;

function InsightsPage() {
  const fn = useServerFn(getAdminInsights);
  const q = useQuery({ queryKey: ["admin-insights"], queryFn: () => fn(), refetchInterval: 60_000 });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Insights automáticos"
        description="Recomendações extraídas do comportamento real dos membros — atualizadas a cada minuto."
      />

      {q.isLoading && <p className="text-sm text-[var(--gray-ink)]">Analisando dados…</p>}

      <div className="grid gap-3 lg:grid-cols-2">
        {(q.data ?? []).map((i, idx) => {
          const cfg = KIND[i.kind];
          const Icon = cfg.icon;
          return (
            <div key={idx} className={`rounded-xl border ${cfg.border} ${cfg.bg} p-4`}>
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 ${cfg.color}`}><Icon size={18} /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="font-display text-sm font-extrabold">{i.title}</p>
                    {i.metric && <span className={`font-display text-lg font-extrabold ${cfg.color}`}>{i.metric}</span>}
                  </div>
                  <p className="mt-1 whitespace-pre-line text-xs text-[var(--gray-ink)]">{i.detail}</p>
                  {i.link && (
                    <Link to={i.link as never} className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--navy)] hover:underline">
                      Investigar <ArrowRight size={11} />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {(q.data ?? []).length === 0 && !q.isLoading && (
          <p className="rounded-xl border border-dashed border-black/10 p-6 text-center text-sm text-[var(--gray-ink)]">
            <TrendingUp className="mx-auto mb-2 text-[var(--green)]" size={20} />
            Nenhum sinal crítico no momento. A plataforma está saudável.
          </p>
        )}
      </div>
    </div>
  );
}
